import { QueryClient } from "@tanstack/react-query";

/**
 * Optimized React Query configuration for e-commerce application
 */
export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Global defaults for all queries
        staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes - cache garbage collection time (formerly cacheTime)
        retry: (failureCount, error: any) => {
          // Don't retry on 4xx errors (client errors)
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          // Retry up to 2 times for other errors
          return failureCount < 2;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
        refetchOnWindowFocus: true, // Refetch when user returns to tab
        refetchOnReconnect: true, // Refetch when internet reconnects
        refetchOnMount: true, // Refetch when component mounts
      },
      mutations: {
        // Global defaults for all mutations
        retry: 1, // Retry failed mutations once
        retryDelay: 1000, // Wait 1 second before retry
      },
    },
  });
};

/**
 * Query keys factory for consistent key management
 */
export const queryKeys = {
  // Products
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.products.lists(), filters] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.products.details(), id] as const,
    search: (query: string) => [...queryKeys.products.all, 'search', query] as const,
    featured: () => [...queryKeys.products.all, 'featured'] as const,
    onSale: () => [...queryKeys.products.all, 'on-sale'] as const,
    recent: () => [...queryKeys.products.all, 'recent'] as const,
    topSelling: (period: string) => [...queryKeys.products.all, 'top-selling', period] as const,
    byCategory: (categoryId: number) => [...queryKeys.products.all, 'category', categoryId] as const,
    bySubCategory: (subCategoryId: number) => [...queryKeys.products.all, 'subcategory', subCategoryId] as const,
  },
  
  // Categories
  categories: {
    all: ['categories'] as const,
    lists: () => [...queryKeys.categories.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.categories.lists(), filters || {}] as const,
    withChildren: () => [...queryKeys.categories.all, 'with-children'] as const,
    withProducts: () => [...queryKeys.categories.all, 'with-products'] as const,
    forFilters: () => [...queryKeys.categories.all, 'for-filters'] as const,
  },

  // User-specific data
  user: {
    all: (userId: string) => ['user', userId] as const,
    cart: (userId: string) => [...queryKeys.user.all(userId), 'cart'] as const,
    wishlist: (userId: string) => [...queryKeys.user.all(userId), 'wishlist'] as const,
    addresses: (userId: string) => [...queryKeys.user.all(userId), 'addresses'] as const,
    orders: (userId: string) => [...queryKeys.user.all(userId), 'orders'] as const,
    profile: (userId: string) => [...queryKeys.user.all(userId), 'profile'] as const,
  },

  // Application data
  app: {
    all: ['app'] as const,
    addressTypes: () => [...queryKeys.app.all, 'address-types'] as const,
    productTypes: () => [...queryKeys.app.all, 'product-types'] as const,
    stages: () => [...queryKeys.app.all, 'stages'] as const,
  },
};

/**
 * Cache time configurations for different data types
 */
export const cacheConfig = {
  // Static/Semi-static data (longer cache times)
  categories: {
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  },
  
  productTypes: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  },

  // Dynamic data (shorter cache times)
  products: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },

  productDetail: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
  },

  // User-specific data (very short cache times)
  userCart: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
  },

  userWishlist: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  },

  // Search results (very short cache)
  search: {
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 2 * 60 * 1000, // 2 minutes
  },

  // Real-time data (no cache)
  realTime: {
    staleTime: 0, // Always stale
    gcTime: 1000, // 1 second
  },
};

/**
 * Optimized query client with retry and error handling
 */
export const queryClient = createQueryClient();

/**
 * Utility function to invalidate related queries
 */
export const invalidateRelatedQueries = {
  // When product is updated, invalidate related queries
  onProductUpdate: (productId: number, categoryId?: number, subCategoryId?: number) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.products.detail(productId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() });
    
    if (categoryId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.byCategory(categoryId) });
    }
    
    if (subCategoryId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.products.bySubCategory(subCategoryId) });
    }
  },

  // When cart is updated
  onCartUpdate: (userId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.user.cart(userId) });
  },

  // When wishlist is updated
  onWishlistUpdate: (userId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.user.wishlist(userId) });
  },

  // When categories are updated (admin operation)
  onCategoryUpdate: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.categories.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.products.lists() }); // Products might be affected
  },

  // When user logs out
  onUserLogout: (userId: string) => {
    queryClient.removeQueries({ queryKey: queryKeys.user.all(userId) });
  },
};

export default queryClient;
